import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { PageHeader } from '@/components/layout/PageHeader';
import { reportsAPI } from '@/lib/api';
import ReportsHub from '@/components/reports/ReportsHub';
import { isKRRole, isAgencyPrincipal, isAgencyContext, getErrorMessage } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  BarChart3, Users, Building2, FileText, Briefcase,
  Download, Loader2, Ticket, TrendingUp,
  Filter, X, FileDown
} from 'lucide-react';

const downloadBlob = (data, filename, type = 'text/csv') => {
  const blob = new Blob([data], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export default function ReportsPage() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ status: '', agency_id: '', npn: '', date_from: '', date_to: '' });

  const isAdmin = isKRRole(user);
  // Agency principals and any user acting inside an agency context
  // may run reports scoped to their own downline. The backend applies
  // the scope filter automatically — the frontend only decides
  // whether to show the page at all.
  const canRunReports = isAdmin || isAgencyPrincipal(user) || isAgencyContext(user);

  useEffect(() => { loadSummary(); }, []);

  const loadSummary = async (filterParams) => {
    setLoading(true);
    try {
      const params = filterParams || {};
      Object.keys(params).forEach(k => { if (!params[k]) delete params[k]; });
      const res = await reportsAPI.summary(params);
      setSummary(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load report summary'));
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    loadSummary(filters);
  };

  const handleClearFilters = () => {
    setFilters({ status: '', agency_id: '', npn: '', date_from: '', date_to: '' });
    loadSummary({});
  };

  const handleExport = async (type, format = 'csv') => {
    const key = `${type}-${format}`;
    setDownloading(prev => ({ ...prev, [key]: true }));
    try {
      let res, filename;
      if (format === 'pdf') {
        const params = {};
        if (filters.status) params.status = filters.status;
        if (filters.agency_id) params.agency_id = filters.agency_id;
        if (filters.npn) params.npn = filters.npn;
        if (filters.date_from) params.date_from = filters.date_from;
        if (filters.date_to) params.date_to = filters.date_to;

        if (type === 'agents') {
          res = await reportsAPI.exportAgentsPDF(params);
          filename = 'agents_roster.pdf';
        } else {
          res = await reportsAPI.exportContractsPDF(params);
          filename = 'contracts_report.pdf';
        }
        downloadBlob(res.data, filename, 'application/pdf');
      } else {
        // Pass the same filters set in the toolbar so the CSV mirrors
        // what the operator sees on-screen (esp. agency + agent dropdowns).
        const params = {};
        if (filters.status) params.status = filters.status;
        if (filters.agency_id) params.agency_id = filters.agency_id;
        if (filters.npn) params.npn = filters.npn;
        if (filters.date_from) params.date_from = filters.date_from;
        if (filters.date_to) params.date_to = filters.date_to;
        switch (type) {
          case 'agents': res = await reportsAPI.exportAgentsCSV(params); filename = 'agents_roster.csv'; break;
          case 'contracts': res = await reportsAPI.exportContractsCSV(params); filename = 'contracts_report.csv'; break;
          case 'agencies': res = await reportsAPI.exportAgenciesCSV(params); filename = 'agencies_report.csv'; break;
          default: return;
        }
        downloadBlob(res.data, filename);
      }
      toast.success(`${filename} downloaded`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to export report'));
    } finally {
      setDownloading(prev => ({ ...prev, [key]: false }));
    }
  };

  if (!canRunReports) {
    return <div className="p-8 text-center text-slate-500">You do not have access to this page.</div>;
  }

  const StatCard = ({ icon: Icon, value, label, color }) => (
    <div className="flex items-center gap-4 p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
      <div className={`p-3 rounded-lg ${color}`}>
        <Icon size={20} className="text-white" />
      </div>
      <div>
        <p className="text-2xl font-heading font-semibold text-slate-900 dark:text-slate-100">{loading ? '-' : (value ?? 0)}</p>
        <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
      </div>
    </div>
  );

  const hasActiveFilters = filters.status || filters.agency_id || filters.npn || filters.date_from || filters.date_to;

  return (
    <div data-testid="reports-page">
      <PageHeader
        title="Reports & Analytics"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Reports', href: '/reports' },
        ]}
        actions={
          <Button variant={showFilters ? 'default' : 'outline'} onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters-btn">
            <Filter size={16} className="mr-2" />
            Filters
            {hasActiveFilters && <Badge className="ml-2 bg-sky-500 text-white text-[10px]">Active</Badge>}
          </Button>
        }
      />

      <div className="page-content">
        {/* Filters Panel */}
        {showFilters && (
          <Card className="mb-6 animate-slideIn" data-testid="filters-panel">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 items-end">
                <div>
                  <Label className="text-xs">Agent Status</Label>
                  <Select value={filters.status} onValueChange={(v) => setFilters(f => ({ ...f, status: v }))}>
                    <SelectTrigger data-testid="filter-status"><SelectValue placeholder="All statuses" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="onboarding">Onboarding</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Agency</Label>
                  <Select
                    value={filters.agency_id}
                    onValueChange={(v) => setFilters(f => ({ ...f, agency_id: v, npn: '' }))}
                  >
                    <SelectTrigger data-testid="filter-agency"><SelectValue placeholder="All agencies" /></SelectTrigger>
                    <SelectContent>
                      {(summary?.agencies_list || []).map(a => (
                        <SelectItem key={a.agency_id} value={a.agency_id}>{a.agency_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Agent</Label>
                  <Select value={filters.npn} onValueChange={(v) => setFilters(f => ({ ...f, npn: v }))}>
                    <SelectTrigger data-testid="filter-agent"><SelectValue placeholder="All agents" /></SelectTrigger>
                    <SelectContent>
                      {(summary?.agents_list || [])
                        .filter(a => !filters.agency_id || a.agency_id === filters.agency_id)
                        .map(a => (
                          <SelectItem key={a.npn} value={a.npn}>
                            {a.full_name} · {a.npn}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Date From</Label>
                  <Input type="date" value={filters.date_from} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} data-testid="filter-date-from" />
                </div>
                <div>
                  <Label className="text-xs">Date To</Label>
                  <Input type="date" value={filters.date_to} onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} data-testid="filter-date-to" />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleApplyFilters} className="flex-1" data-testid="apply-filters-btn">Apply</Button>
                  {hasActiveFilters && (
                    <Button variant="outline" onClick={handleClearFilters} data-testid="clear-filters-btn">
                      <X size={14} />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          <StatCard icon={Users} value={summary?.total_agents} label="Total Agents" color="bg-sky-600" />
          <StatCard icon={Building2} value={summary?.total_agencies} label="Agencies" color="bg-slate-800" />
          <StatCard icon={FileText} value={summary?.total_requests} label="Contracts" color="bg-amber-500" />
          <StatCard icon={Briefcase} value={summary?.approved_appointments} label="Active Appts" color="bg-emerald-600" />
          <StatCard icon={Ticket} value={summary?.open_tickets} label="Open Tickets" color="bg-rose-500" />
        </div>

        {/* Agent Breakdown */}
        {summary && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <TrendingUp size={16} className="text-sky-500" />
                Agent Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 text-center">
                  <p className="text-2xl font-semibold text-emerald-700 dark:text-emerald-400">{summary.active_agents}</p>
                  <p className="text-xs text-emerald-600 dark:text-emerald-500 mt-1">Active</p>
                </div>
                <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/30 text-center">
                  <p className="text-2xl font-semibold text-amber-700 dark:text-amber-400">{summary.onboarding_agents}</p>
                  <p className="text-xs text-amber-600 dark:text-amber-500 mt-1">Onboarding</p>
                </div>
                <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-700/30 text-center">
                  <p className="text-2xl font-semibold text-slate-700 dark:text-slate-300">{summary.inactive_agents}</p>
                  <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">Inactive</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contract Status Breakdown */}
        {summary?.requests_by_status && Object.keys(summary.requests_by_status).length > 0 && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <BarChart3 size={16} className="text-amber-500" />
                Contract Status Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {Object.entries(summary.requests_by_status).map(([status, count]) => (
                  <div key={status} className="p-3 rounded-lg bg-slate-50 dark:bg-slate-700/30 text-center">
                    <Badge variant="outline" className="text-[10px] mb-1">{status.replace(/_/g, ' ')}</Badge>
                    <p className="text-xl font-semibold text-slate-900 dark:text-slate-100">{count}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Unified Reports Hub — AI assistant + all report types + export */}
        <ReportsHub />
      </div>
    </div>
  );
}
